import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Calendar, Clock, MapPin } from 'lucide-react'
import { supabase } from '../lib/supabase'

interface UpcomingJobRow {
  id: string
  job_number: string
  title: string
  scheduled_date: string
  status: string
  scheduled_start_time?: string | null
  property?: { name?: string; address?: string } | null
}

interface UpcomingJobsModalProps {
  isOpen: boolean
  onClose: () => void
  jobId: string
  jobData?: any
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const UpcomingJobsModal: React.FC<UpcomingJobsModalProps> = ({
  isOpen,
  onClose,
  jobId,
  jobData,
}) => {
  const navigate = useNavigate()
  const [upcomingJobs, setUpcomingJobs] = useState<UpcomingJobRow[]>([])
  const [loading, setLoading] = useState(false)
  const [daysAhead, setDaysAhead] = useState(30)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const startStr = today.toISOString().split('T')[0]
        const endDate = new Date(today)
        endDate.setDate(today.getDate() + daysAhead)
        const endStr = endDate.toISOString().split('T')[0]

        let contractId = jobData?.contract_id || null
        let propertyId = jobData?.property_id || null

        if (!contractId && jobId) {
          const { data: job } = await supabase
            .from('jobs')
            .select('contract_id, property_id')
            .eq('id', jobId)
            .maybeSingle()
          contractId = job?.contract_id || null
          propertyId = propertyId || job?.property_id || null
        }

        let query = supabase
          .from('jobs')
          .select(
            'id, job_number, title, scheduled_date, status, scheduled_start_time, property:properties(name, address)'
          )
          .gte('scheduled_date', startStr)
          .lte('scheduled_date', endStr)
          .neq('status', 'cancelled')
          .order('scheduled_date', { ascending: true })

        if (contractId) {
          query = query.eq('contract_id', contractId)
        } else if (propertyId) {
          query = query.eq('property_id', propertyId)
        } else {
          query = query.eq('id', jobId)
        }

        const { data, error: qErr } = await query
        if (qErr) throw qErr

        setUpcomingJobs(
          (data || []).map((row: any) => ({
            ...row,
            property: Array.isArray(row.property) ? row.property[0] || null : row.property,
          }))
        )
      } catch (err: any) {
        setError(err?.message || 'Failed to load upcoming jobs')
        setUpcomingJobs([])
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [isOpen, jobId, jobData, daysAhead])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Upcoming Jobs</h2>
            <p className="text-sm text-gray-600 mt-1">
              Scheduled jobs related to {jobData?.job_number || 'this job'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Show next:</label>
              <select
                value={daysAhead}
                onChange={(e) => setDaysAhead(Number(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
            <div className="text-sm text-gray-500">{upcomingJobs.length} upcoming jobs</div>
          </div>

          {error && (
            <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded px-3 py-2">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {upcomingJobs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No upcoming jobs found in this window</p>
                </div>
              ) : (
                upcomingJobs.map((job) => {
                  const date = new Date(job.scheduled_date + 'T00:00:00')
                  return (
                    <button
                      key={job.id}
                      type="button"
                      onClick={() => {
                        onClose()
                        navigate(`/jobs/details/${job.id}`)
                      }}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors text-left"
                    >
                      <div className="flex items-center space-x-4 min-w-0">
                        <div className="flex items-center space-x-2 shrink-0">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <div className="text-sm font-medium text-gray-900">
                            {date.toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 truncate">
                          #{job.job_number} · {job.title}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 shrink-0">
                        {(job.property?.name || job.property?.address) && (
                          <span className="hidden sm:inline-flex items-center gap-1 text-xs text-gray-500">
                            <MapPin className="w-3 h-3" />
                            {job.property?.name || job.property?.address}
                          </span>
                        )}
                        <div className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                          {DAY_NAMES[date.getDay()]}
                        </div>
                        <div className="text-xs text-gray-500 capitalize">
                          {job.status.replace(/_/g, ' ')}
                        </div>
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          )}

          {upcomingJobs.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-medium text-blue-800">Schedule Summary</h4>
              </div>
              <div className="text-sm text-blue-700">
                <p>
                  <strong>Next Job:</strong>{' '}
                  {new Date(upcomingJobs[0].scheduled_date + 'T00:00:00').toLocaleDateString(
                    'en-US',
                    { weekday: 'long', month: 'short', day: 'numeric' }
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default UpcomingJobsModal
